import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Share2, FileText, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CAR_DIAGRAM, CAR_VB_W, CAR_VB_H } from "@/lib/carDiagram";
import { INTERIOR_DIAGRAM } from "@/lib/interiorDiagram";
import { INT_VB_W, INT_VB_H } from "@/lib/panels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LABELS = ["Scratch", "Dent", "Repaint Mark", "Stain", "Custom"];
const COLORS = { Scratch: "#F5185B", Dent: "#7c3aed", "Repaint Mark": "#2563eb", Stain: "#0d9488", Custom: "#ea580c" };
const color = (l) => COLORS[l] || "#ea580c";

// Rasterise an <svg> DOM node (incl. inline images / shapes) to a JPEG data URI.
const rasterizeSvg = (svgNode, w, h) =>
  new Promise((resolve) => {
    if (!svgNode) return resolve(null);
    const svg = svgNode.cloneNode(true);
    svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    const xml = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = w * 2; c.height = h * 2;
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
      ctx.drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = () => resolve(null);
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(xml)));
  });

export default function Inspection() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  return <InspectionBoard loadUrl={`/jobs/${jobId}/inspection`} saveUrl={`/jobs/${jobId}/inspection/generate`}
    onBack={() => navigate(`/jobs/${jobId}`)} />;
}

export function InspectionBoard({ loadUrl, saveUrl, onBack, header, onSaved }) {
  const extRef = useRef();
  const intRef = useRef();
  const [view, setView] = useState("exterior");
  const [extPins, setExtPins] = useState([]);
  const [intPins, setIntPins] = useState([]);
  const [active, setActive] = useState("Scratch");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(loadUrl).then(({ data }) => {
      if (data) {
        setExtPins(data.exterior_pins?.length ? data.exterior_pins : (data.pins || []));
        setIntPins(data.interior_pins || []);
        setNote(data.note || "");
      }
    }).catch(() => {});
  }, [loadUrl]);

  const isExt = view === "exterior";
  const pins = isExt ? extPins : intPins;
  const setPins = isExt ? setExtPins : setIntPins;

  const addPin = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    let label = active;
    if (active === "Custom") label = window.prompt("Describe the mark:") || "Custom";
    setPins((p) => [...p, { x, y, label, loc: "" }]);
  };
  const removePin = (e, idx) => { e.stopPropagation(); setPins((p) => p.filter((_, i) => i !== idx)); };
  const undoPin = () => setPins((p) => p.slice(0, -1));
  const setLoc = (idx, loc) => setPins((p) => p.map((pin, i) => (i === idx ? { ...pin, loc } : pin)));

  const generate = async () => {
    setBusy(true);
    try {
      const exterior_image = await rasterizeSvg(extRef.current, CAR_VB_W, CAR_VB_H);
      const interior_image = await rasterizeSvg(intRef.current, INT_VB_W, INT_VB_H);
      const { data } = await api.post(saveUrl, {
        pins: extPins, exterior_pins: extPins, interior_pins: intPins, note,
        diagram_image: exterior_image, exterior_image, interior_image,
      });
      const reportUrl = `${API}/inspections/${data.id}/report.pdf`;
      const phone = (data.customer_phone || "").replace(/\D/g, "");
      const waPhone = phone ? (phone.length === 10 ? "91" + phone : phone) : "";
      const msg = `Hi ${data.customer_name || ""}, here is your Veloskin PPF vehicle inspection report: ${reportUrl}`;
      const wa = waPhone ? `https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}` : "";
      setResult({ reportUrl, wa, hasPhone: !!phone });
      toast.success("Report generated");
      onSaved?.(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const Pin = ({ p, i, vbW, vbH }) => (
    <g onClick={(e) => removePin(e, i)} style={{ cursor: "pointer" }}>
      <circle cx={p.x * vbW} cy={p.y * vbH} r={vbW / 37} fill={color(p.label)} stroke="#fff" strokeWidth="4" />
      <text x={p.x * vbW} y={p.y * vbH + vbW / 105} textAnchor="middle" fontSize={vbW / 33} fontWeight="bold" fill="#fff">{i + 1}</text>
    </g>
  );

  return (
    <div className="mx-auto min-h-screen w-full max-w-md overflow-x-hidden bg-background pb-6">
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-brand-line bg-white/95 px-4 py-3 backdrop-blur">
        <button onClick={onBack} data-testid="inspection-back" className="text-brand">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="font-display text-base font-semibold text-brand-ink">Vehicle Inspection</h1>
      </div>
      {header}

      <div className="px-4 pt-4">
        {result && (
          <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="inspection-result">
            <p className="font-medium text-emerald-800">Report ready.</p>
            <a href={result.reportUrl} target="_blank" rel="noreferrer" className="mt-2 block" data-testid="inspection-view-pdf">
              <Button className="h-11 w-full gap-2 bg-brand hover:bg-brand/90">
                <FileText className="h-4 w-4" /> View / Download PDF
              </Button>
            </a>
          </div>
        )}

        <div className="mb-3 grid grid-cols-2 gap-1 rounded-lg bg-brand-tint p-1">
          {["exterior", "interior"].map((v) => (
            <button key={v} data-testid={`view-${v}`} onClick={() => setView(v)}
              className={`rounded-md py-2 text-xs font-semibold capitalize transition-colors ${view === v ? "bg-brand text-white" : "text-brand"}`}>
              {v} ({v === "exterior" ? extPins.length : intPins.length})
            </button>
          ))}
        </div>

        <div className="mb-2 flex flex-wrap items-center gap-2">
          {LABELS.map((l) => (
            <button key={l} data-testid={`label-${l}`} onClick={() => setActive(l)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold ${active === l ? "text-white" : "bg-brand-tint text-brand"}`}
              style={active === l ? { backgroundColor: color(l) } : {}}>{l}</button>
          ))}
          <button data-testid="inspection-undo" onClick={undoPin} disabled={pins.length === 0}
            className="ml-auto inline-flex items-center gap-1 rounded-full border border-brand-line px-3 py-1.5 text-xs font-semibold text-brand disabled:opacity-40">
            <Undo2 className="h-3.5 w-3.5" /> Undo
          </button>
        </div>
        <p className="mb-2 text-xs text-slate-500">Tap the diagram to drop a <b style={{ color: color(active) }}>{active}</b> pin · add a location note if needed · tap a pin to remove it</p>

        {/* Exterior diagram */}
        <div className={`w-full overflow-hidden rounded-xl border border-brand-line bg-white ${isExt ? "" : "hidden"}`}>
          <svg ref={extRef} data-testid="inspection-diagram-exterior"
            viewBox={`0 0 ${CAR_VB_W} ${CAR_VB_H}`} className="block w-full" style={{ touchAction: "manipulation" }}
            onClick={addPin}>
            <rect x="0" y="0" width={CAR_VB_W} height={CAR_VB_H} fill="#ffffff" />
            <image href={CAR_DIAGRAM} x="0" y="0" width={CAR_VB_W} height={CAR_VB_H} preserveAspectRatio="xMidYMid meet" />
            {extPins.map((p, i) => <Pin key={i} p={p} i={i} vbW={CAR_VB_W} vbH={CAR_VB_H} />)}
          </svg>
        </div>

        {/* Interior diagram */}
        <div className={`w-full overflow-hidden rounded-xl border border-brand-line bg-white ${isExt ? "hidden" : ""}`}>
          <svg ref={intRef} data-testid="inspection-diagram-interior"
            viewBox={`0 0 ${INT_VB_W} ${INT_VB_H}`} className="block w-full" style={{ touchAction: "manipulation" }}
            onClick={addPin}>
            <rect x="0" y="0" width={INT_VB_W} height={INT_VB_H} fill="#ffffff" />
            <image href={INTERIOR_DIAGRAM} x="0" y="0" width={INT_VB_W} height={INT_VB_H} preserveAspectRatio="xMidYMid meet" />
            {intPins.map((p, i) => <Pin key={i} p={p} i={i} vbW={INT_VB_W} vbH={INT_VB_H} />)}
          </svg>
        </div>

        {pins.length > 0 && (
          <div className="mt-3 space-y-2">
            {pins.map((p, i) => (
              <div key={i} className="flex items-center gap-2 text-xs" data-testid={`pin-row-${i}`}>
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: color(p.label) }}>{i + 1}</span>
                <span className="w-24 shrink-0 font-semibold text-slate-700">{p.label}</span>
                <input
                  data-testid={`pin-loc-${i}`}
                  value={p.loc || ""}
                  onChange={(e) => setLoc(i, e.target.value)}
                  placeholder="location (optional)"
                  className="h-8 flex-1 rounded-md border border-brand-line bg-white px-2 text-xs text-slate-700 focus:border-brand focus:outline-none"
                />
              </div>
            ))}
          </div>
        )}

        <h2 className="font-display mb-2 mt-5 text-lg font-semibold text-brand-ink">Note to customer</h2>
        <Textarea data-testid="inspection-note" value={note} onChange={(e) => setNote(e.target.value)} rows={4}
          placeholder="This note appears in the report sent to the customer." />
      </div>

      <div className="mx-4 mb-2 mt-3 flex gap-2 rounded-xl border border-brand-line bg-white p-3 shadow-sm">
        {result ? (
          <>
            {result.hasPhone ? (
              <a href={result.wa} target="_blank" rel="noreferrer" className="flex-1" data-testid="inspection-whatsapp">
                <Button className="h-12 w-full bg-emerald-600 hover:bg-emerald-700"><Share2 className="mr-2 h-5 w-5" /> Share via WhatsApp</Button>
              </a>
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex-1" data-testid="inspection-whatsapp-disabled">
                      <Button
                        disabled
                        title="No customer phone number available"
                        className="h-12 w-full bg-emerald-600"
                      >
                        <Share2 className="mr-2 h-5 w-5" /> Share via WhatsApp
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>No customer phone number available</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <Button variant="outline" className="h-12" onClick={generate} disabled={busy}>Re-generate</Button>
          </>
        ) : (
          <Button className="h-12 w-full" onClick={generate} disabled={busy} data-testid="inspection-generate">
            <FileText className="mr-2 h-5 w-5" /> {busy ? "Generating…" : "Generate Report"}
          </Button>
        )}
      </div>
    </div>
  );
}
